import * as THREE from 'three';
import { clamp, hash01, hash3, idSeed, lerp, mulberry32, smoothstep } from '@/core/rng';
import type { BuildingPractice } from '@/sim/culture';

/**
 * Structures — habitation made visible (spec §40–§48). A hearth is a ring of
 * stones with an ember and a smoke column; a shelter is a hide stretched over a
 * ridge pole; a storage rack is thin legs under a box. Around all three lies
 * the litter of use — stacked timber, drying frames, windbreaks — instanced and
 * hashed from the structures themselves. One instanced mesh per kind.
 *
 * Render-only, like the clouds and the fire FX: this module reads plain
 * StructureViews the integrator maps from sim state and owns nothing. Every
 * irregularity is hashed from the structure's own id, so the same camp looks
 * the same on every machine, every frame, from every direction — and the
 * update path allocates nothing.
 *
 * The smoke column is the spec-§48 distant signal: shelters vanish under the
 * pull-back at ~8 km, but a living hearth's smoke grows with its own distance
 * from the eye — so it holds its apparent size, and one camp says *someone
 * lives here* from a hillside away or from the far side of the island.
 */

/** Stable 32-bit hash of a structure id — the seed for all its irregularity. */
// one definition, in core/rng.ts, because the simulation needs it too
export { idSeed } from '@/core/rng';

export interface StructureView {
  id: string;
  type: 'hearth' | 'shelter' | 'storage';
  x: number; y: number; z: number;
  /** 0..1 upkeep. Below ~0.3 the structure reads as a ruin. */
  condition: number;
  active: boolean;
  /**
   * The roof shape its builders raised (§50). Absent on structures from a
   * world older than the practice, which get `DEFAULT_BUILDING`.
   */
  built?: BuildingPractice;
}

/**
 * Where the eye is, and which way it is turned. The smoke is the only thing
 * here that needs to know: every other piece of a camp stands in the world
 * facing whatever it faces, and a puff of smoke has no facing of its own.
 */
export interface EyeView {
  x: number; y: number; z: number;
  /** The camera's world rotation — the plane a puff is drawn on. */
  quaternion: THREE.Quaternion;
}

export interface Structures {
  group: THREE.Group;
  update(tSec: number, views: StructureView[], camR: number, eye: EyeView): void;
}

const CAP = 128;                  // structures of each kind
const STONES = 6;                 // ring stones per hearth (5 or 6 used)
const STONE_CAP = CAP * STONES;
const PUFFS = 16;                 // recycling puffs per active hearth
const SMOKE_CAP = CAP * PUFFS;
const PROP_CAP = 192;             // props of each kind, across the whole world
const RUIN = 0.3;                 // below this, a structure is a ruin
/**
 * Diorama scale, matching the figures in render/mortals.ts. A true 1.8 m
 * lean-to is a couple of tan pixels from anywhere you can also see the
 * hillside it stands on, so a whole settlement disappears at exactly the
 * range the game is played. Doubling makes a camp a place. The simulation's
 * distances — hearth range, serve range — are untouched and still true.
 */
const MODEL = 2.0;
const NEAR = 8000;                // structures are valley-scale; smoke is not
/**
 * Props are a metre or two of firewood and lashed poles: past this they are
 * fill rate spent on nothing. They are the camp's texture, not its signal.
 */
const PROP_NEAR = 2600;
/** A shelter looks to the nearest hearth this far off to decide which way it faces. */
const CAMP_REACH = 100;
const CYCLE = 24;                 // seconds for one puff to climb the column
const TAU = Math.PI * 2;
/**
 * The column's habitual lean, metres sideways per metre risen — one sky, one
 * prevailing westerly, the same drift the clouds ride.
 *
 * Steep enough to matter: a column that stands straight up is seen end-on from
 * directly above, which is exactly where a god spends its time, and a 230 m
 * plume collapses into a white speck. Laid over downwind it becomes a streak
 * across the ground instead — legible from above, from the side, and from
 * every angle between, and every plume on the island leans the same way, so
 * what the eye reads is weather rather than decoration.
 */
const LEAN_X = 0.76, LEAN_Z = 0.23;
/** Metres a wood fire's column climbs before it gives up and disperses. */
const RISE = 46;
/**
 * How far away the plume stops being its own size and starts holding its
 * apparent one, and how far that bargain runs.
 *
 * Beyond PLUME_HOLD the column grows in step with its distance, which freezes
 * how big it looks: a camp eight kilometres off shows the same smudge of smoke
 * as a camp one kilometre off, and both are unmistakably smoke. That is the
 * whole trade — a column that stays honestly forty-six metres tall is four
 * pixels at three kilometres and simply is not there, and "is anyone over
 * there?" is the question the game is played by (§13).
 *
 * It is the same render-only exaggeration the figures and the buildings
 * already make (FIGURE_SCALE, MODEL): the *simulation* never hears about it,
 * the hearth stays exactly where it was built, and nothing about the world's
 * state depends on how far away somebody happens to be standing.
 *
 * The number is measured, not guessed: RISE / PLUME_HOLD is the fraction of
 * the viewport the column holds, and at 46/600 that is about a twelfth of the
 * frame — eighty pixels of smoke, from anywhere. Two kilometres out at the
 * first value tried it was forty pixels of half-transparent grey against a
 * sunlit hillside, which photographs as nothing at all.
 */
const PLUME_HOLD = 600, PLUME_MAX = 30;

/** Non-indexed concatenation, the mortals.ts trick — position and normal only. */
function merge(parts: THREE.BufferGeometry[]): THREE.BufferGeometry {
  const geo = new THREE.BufferGeometry();
  for (const name of ['position', 'normal'] as const) {
    const arrs = parts.map((p) => p.attributes[name].array as Float32Array);
    const all = new Float32Array(arrs.reduce((s, a) => s + a.length, 0));
    let o = 0;
    for (const a of arrs) { all.set(a, o); o += a.length; }
    geo.setAttribute(name, new THREE.BufferAttribute(all, 3));
  }
  parts.forEach((p) => p.dispose());
  return geo;
}

/**
 * The clouds.ts soft-blob trick — offset radial falloffs — computed straight
 * into a DataTexture rather than a canvas, so the module needs no DOM and
 * builds identically in the browser and under headless tests.
 */
function smokeTexture(): THREE.DataTexture {
  const s = 64;
  const rnd = mulberry32(9151);
  const blobs: Array<[number, number, number]> = [];
  for (let i = 0; i < 4; i++) {
    blobs.push([s * (0.34 + rnd() * 0.32), s * (0.34 + rnd() * 0.32), s * (0.2 + rnd() * 0.18)]);
  }
  const data = new Uint8Array(s * s * 4);
  for (let y = 0; y < s; y++) for (let x = 0; x < s; x++) {
    let a = 0;
    for (const [bx, by, br] of blobs) {
      const t = Math.max(0, 1 - Math.hypot(x - bx, y - by) / br);
      a += t * t * 0.6;
    }
    const k = (y * s + x) * 4;
    data[k] = data[k + 1] = data[k + 2] = 255;
    data[k + 3] = Math.min(1, a) * 255;
  }
  const tex = new THREE.DataTexture(data, s, s);
  tex.magFilter = tex.minFilter = THREE.LinearFilter;
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.needsUpdate = true;
  return tex;
}

/** Thin legs under a box — a raised rack, safe from small game. */
function rackGeometry(): THREE.BufferGeometry {
  const leg = (x: number, z: number) =>
    new THREE.BoxGeometry(0.09, 0.85, 0.09).toNonIndexed().translate(x, 0.425, z);
  return merge([
    leg(-0.52, -0.3), leg(0.52, -0.3), leg(-0.52, 0.3), leg(0.52, 0.3),
    new THREE.BoxGeometry(1.3, 0.42, 0.85).toNonIndexed().translate(0, 1.06, 0),
  ]).scale(MODEL, MODEL, MODEL);
}

type P3 = readonly [number, number, number];

/**
 * A flat quad with a lining — six triangles, both faces, one hard normal each.
 * Skins get both windings on purpose: the shelter has an open doorway, and
 * through it you should see the inside of the far wall, not the hillside.
 */
function skin(a: P3, b: P3, c: P3, d: P3): THREE.BufferGeometry {
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(new Float32Array([
    ...a, ...b, ...c, ...a, ...c, ...d,
    ...a, ...c, ...b, ...a, ...d, ...c,
  ]), 3));
  g.computeVertexNormals();
  return g;
}

/** The same, triangular — a gable end. */
function gable(a: P3, b: P3, c: P3): THREE.BufferGeometry {
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(new Float32Array([
    ...a, ...b, ...c, ...a, ...c, ...b,
  ]), 3));
  g.computeVertexNormals();
  return g;
}

/**
 * The shelter (spec §41): a hide stretched over a ridge pole, the pole
 * overhanging both gables, crossed door-poles at the front, a doorway under the
 * front gable and the back closed against the weather. Local +Z is the
 * doorway — update() yaws that toward the camp's fire.
 *
 * This replaced a seven-sided open cone. The cone was 7 triangles and read,
 * honestly, as a small triangle: one smooth silhouette, one flat shade, no
 * evidence of a builder. Flat panels are the entire point of the change — the
 * lit flank, the shaded flank and the crease between them give the eye three
 * tones and a ridge line, which is what survives at 400 m when the whole
 * shelter is twelve pixels tall. 56 triangles buys a building.
 *
 * Now parameterised, because the roof is no longer one shape. `building` is
 * one of §50's six practices and the most legible of them from the air, so
 * this is the ridge form — used at hut proportions by nobody and at length by
 * the longhouse builders — and `roundGeometry`, `sunkenGeometry` and
 * `leanToGeometry` below are the other three.
 */
function ridgeGeometry(L: number, W: number, H: number): THREE.BufferGeometry {
  const MW = W * 0.58, MH = H * 0.66;  // the crease, where the taut hide bulges
  const parts: THREE.BufferGeometry[] = [];
  for (const s of [1, -1]) {
    parts.push(skin([0, H, -L], [0, H, L], [s * MW, MH, L], [s * MW, MH, -L]));
    parts.push(skin([s * MW, MH, -L], [s * MW, MH, L], [s * W, 0, L], [s * W, 0, -L]));
  }
  // the back is closed; the front keeps only its peak, and the gap below it
  // is the door — a dark notch, which is how a dwelling reads from the air
  parts.push(gable([0, H, -L], [-W, 0, -L], [W, 0, -L]));
  parts.push(gable([0, H, L], [-W * 0.46, H * 0.46, L], [W * 0.46, H * 0.46, L]));
  // the ridge pole, overhanging both ends: the plainest possible statement
  // that hands made this
  parts.push(new THREE.BoxGeometry(0.1, 0.1, 2 * L + 0.9).toNonIndexed()
    .translate(0, H + 0.02, 0.1));
  // and the crossed poles lashed above the door
  for (const s of [1, -1]) {
    parts.push(new THREE.BoxGeometry(0.08, 2.1 * (H / 1.55), 0.08).toNonIndexed()
      .rotateZ(s * 0.55).translate(s * W * 0.46, 0.855 * (H / 1.55), L - 0.05));
  }
  return merge(parts).scale(MODEL, MODEL, MODEL);
}

/**
 * **round** — a thatched cone on a low wall, nine flat panels of it.
 *
 * The signature from directly above is the one thing no other variant has: a
 * circle, and nine creases running out of its middle like the spokes of a
 * wheel. Every other roof here is a rectangle from the air.
 */
function roundGeometry(): THREE.BufferGeometry {
  const R = 1.30, WALL = 0.42, H = 1.78;
  const N = 9;
  const parts: THREE.BufferGeometry[] = [];
  const at = (a: number, y: number): P3 => [Math.cos(a) * R, y, Math.sin(a) * R];
  for (let i = 0; i < N; i++) {
    const a0 = (i / N) * TAU, a1 = ((i + 1) / N) * TAU;
    // the doorway: the one panel facing +Z keeps its roof and loses its wall,
    // so the opening reads as a dark notch under the eaves
    const facesDoor = Math.abs(Math.atan2(
      Math.sin((a0 + a1) / 2 - Math.PI / 2), Math.cos((a0 + a1) / 2 - Math.PI / 2))) < TAU / (2 * N);
    if (!facesDoor) {
      parts.push(skin(at(a0, 0), at(a1, 0), at(a1, WALL), at(a0, WALL)));
    }
    parts.push(gable(at(a0, WALL), at(a1, WALL), [0, H, 0]));
  }
  // the ring beam the rafters are lashed to, and a stub of the centre post
  parts.push(new THREE.CylinderGeometry(R * 1.02, R * 1.02, 0.07, N, 1).toNonIndexed()
    .translate(0, WALL, 0));
  parts.push(new THREE.BoxGeometry(0.09, H, 0.09).toNonIndexed().translate(0, H / 2, 0));
  return merge(parts).scale(MODEL, MODEL, MODEL);
}

/**
 * **sunken** — dug in, so what stands above the ground is a broad low pyramid
 * of a roof sitting almost on the earth, with the spoil banked around it.
 *
 * The tell from the air is that it is *wide and flat*: four creases meeting at
 * a point in the middle of a square, where the ridge builders give two long
 * slopes and a line. It also throws almost no shadow, which is the honest
 * consequence of being half underground.
 */
function sunkenGeometry(): THREE.BufferGeometry {
  const S = 1.42;              // half-width of the roof square — 5.7 m across
  const EAVE = 0.26, H = 0.92; // eaves nearly on the ground; a shallow peak
  const parts: THREE.BufferGeometry[] = [];
  const c: P3[] = [[-S, EAVE, -S], [S, EAVE, -S], [S, EAVE, S], [-S, EAVE, S]];
  for (let i = 0; i < 4; i++) parts.push(gable(c[i], c[(i + 1) % 4], [0, H, 0]));
  // the bank of spoil: a low skirt from the eaves down to the ground, which is
  // the earth that came out of the hole
  for (let i = 0; i < 4; i++) {
    const a = c[i], b = c[(i + 1) % 4];
    parts.push(skin(a, b,
      [b[0] * 1.22, 0, b[2] * 1.22], [a[0] * 1.22, 0, a[2] * 1.22]));
  }
  // the way in: a cut through the bank on the door side, and a lintel over it
  parts.push(new THREE.BoxGeometry(0.62, 0.10, 0.10).toNonIndexed()
    .translate(0, EAVE + 0.06, S * 1.16));
  for (const s of [1, -1]) {
    parts.push(new THREE.BoxGeometry(0.08, EAVE + 0.1, 0.08).toNonIndexed()
      .translate(s * 0.30, (EAVE + 0.1) / 2, S * 1.16));
  }
  return merge(parts).scale(MODEL, MODEL, MODEL);
}

/**
 * **lean-to** — one pitch, propped high at the front and running down to the
 * ground at the back. The front is simply open.
 *
 * This is the one roof with no ridge at all, and that is exactly what makes it
 * legible: every other variant gives the eye a crease with a lit side and a
 * shaded side, and this one is a single flat plane holding a single tone. From
 * above it reads as a plain bright rectangle among creased ones.
 */
function leanToGeometry(): THREE.BufferGeometry {
  const W = 1.24, L = 1.46;
  const H = 1.62, BACK = 0.20;
  const parts: THREE.BufferGeometry[] = [];
  // the pitch itself, front-high to back-low
  parts.push(skin([-W, H, L], [W, H, L], [W, BACK, -L], [-W, BACK, -L]));
  // the two sides, each a quadrilateral under the slope
  for (const s of [1, -1]) {
    parts.push(skin([s * W, 0, L], [s * W, H, L], [s * W, BACK, -L], [s * W, 0, -L]));
  }
  // the back, closed against the weather; the front stays open — that is the
  // whole of what a lean-to is
  parts.push(skin([-W, 0, -L], [W, 0, -L], [W, BACK, -L], [-W, BACK, -L]));
  // the two front posts holding the beam up, and the beam
  for (const s of [1, -1]) {
    parts.push(new THREE.BoxGeometry(0.1, H, 0.1).toNonIndexed()
      .translate(s * W * 0.94, H / 2, L));
  }
  parts.push(new THREE.BoxGeometry(2 * W + 0.5, 0.1, 0.1).toNonIndexed()
    .translate(0, H + 0.02, L));
  return merge(parts).scale(MODEL, MODEL, MODEL);
}

/**
 * The four roofs, by the practice that raises them. Keyed by the exact strings
 * in `PRACTICES.building`, so a people's practice *is* the lookup — nothing
 * maps or translates, and a variant added to culture without a roof here is a
 * type error rather than a silently missing building.
 */
const SHELTER_GEOMETRY: Record<BuildingPractice, () => THREE.BufferGeometry> = {
  round: roundGeometry,
  // a longhouse is the ridge form at length: L is a *half*-length, so 3.40 is
  // 13.6 m of roof on a 4.1 m span — from the air a bar where the others are
  // blobs, and the one silhouette that cannot be mistaken at any altitude
  longhouse: () => ridgeGeometry(3.40, 1.02, 1.48),
  sunken: sunkenGeometry,
  'lean-to': leanToGeometry,
};

/** The roof a structure with no recorded practice gets — see StructureState.built. */
const DEFAULT_BUILDING: BuildingPractice = 'round';

/**
 * Stacked timber — three logs and one on top, the way a stack actually sits.
 * Every camp burns wood; the renderer cannot see the band's reserve and has no
 * business knowing it, so a pile is simply what stands beside a fire.
 */
function woodpileGeometry(): THREE.BufferGeometry {
  const log = (z: number, y: number, spin: number, dx: number) =>
    new THREE.CylinderGeometry(0.11, 0.1, 1.45, 5, 1).toNonIndexed()
      .rotateZ(Math.PI / 2).rotateY(spin).translate(dx, y, z);
  return merge([
    log(-0.24, 0.11, 0.05, 0.04), log(0, 0.11, -0.04, -0.03),
    log(0.24, 0.115, 0.09, 0.02), log(-0.1, 0.31, -0.07, -0.05),
  ]).scale(MODEL, MODEL, MODEL);
}

/** A drying frame: two uprights, two crossbars, and something hung to cure. */
function frameGeometry(): THREE.BufferGeometry {
  const post = (x: number, lean: number) =>
    new THREE.BoxGeometry(0.075, 1.25, 0.075).toNonIndexed()
      .rotateZ(lean).translate(x, 0.62, 0);
  const bar = (y: number) =>
    new THREE.BoxGeometry(1.9, 0.055, 0.055).toNonIndexed().translate(0, y, 0);
  return merge([
    post(-0.85, 0.055), post(0.85, -0.05), bar(1.2), bar(0.74),
    new THREE.BoxGeometry(0.36, 0.44, 0.03).toNonIndexed().translate(-0.3, 0.97, 0),
  ]).scale(MODEL, MODEL, MODEL);
}

/** A hide stretched between two posts — a windbreak, back to the wind. */
function windbreakGeometry(): THREE.BufferGeometry {
  const post = (x: number) =>
    new THREE.BoxGeometry(0.08, 1.05, 0.08).toNonIndexed().translate(x, 0.52, 0);
  return merge([
    post(-0.9), post(0.9),
    new THREE.BoxGeometry(1.75, 0.92, 0.05).toNonIndexed()
      .rotateX(0.2).translate(0, 0.5, -0.1),
  ]).scale(MODEL, MODEL, MODEL);
}

export function createStructures(): Structures {
  const group = new THREE.Group();
  // one matte material for everything solid; instance colour carries the rest
  const matte = new THREE.MeshStandardMaterial({ roughness: 0.95, metalness: 0 });

  const stones = new THREE.InstancedMesh(
    new THREE.DodecahedronGeometry(0.16 * MODEL, 0), matte, STONE_CAP);
  // One instanced mesh per roof shape. Four draw calls instead of one is the
  // whole price of a people looking like itself, and each is capped
  // independently — a valley of longhouse builders does not spend the round
  // builders' budget.
  const shelterKinds = Object.keys(SHELTER_GEOMETRY) as BuildingPractice[];
  const shelterMesh = {} as Record<BuildingPractice, THREE.InstancedMesh>;
  const nShelterOf = {} as Record<BuildingPractice, number>;
  for (const k of shelterKinds) {
    shelterMesh[k] = new THREE.InstancedMesh(SHELTER_GEOMETRY[k](), matte, CAP);
    shelterMesh[k].name = `shelters:${k}`;
  }
  const storages = new THREE.InstancedMesh(rackGeometry(), matte, CAP);
  // Shelters, racks and stones cast. A roof is the largest made thing in this
  // world and the one whose lack of a shadow read most obviously as a model
  // resting on a surface rather than a building standing on ground.
  for (const m of [stones, ...Object.values(shelterMesh)]) {
    m.castShadow = true; m.receiveShadow = true;
  }
  storages.castShadow = true; storages.receiveShadow = true;
  // the litter of use — every camp's real evidence of being lived in
  const woodpiles = new THREE.InstancedMesh(woodpileGeometry(), matte, PROP_CAP);
  const frames = new THREE.InstancedMesh(frameGeometry(), matte, PROP_CAP);
  const windbreaks = new THREE.InstancedMesh(windbreakGeometry(), matte, PROP_CAP);
  const embers = new THREE.InstancedMesh(
    new THREE.CircleGeometry(0.55, 10).rotateX(-Math.PI / 2),
    new THREE.MeshBasicMaterial({
      transparent: true, opacity: 0.85, depthWrite: false,
      blending: THREE.AdditiveBlending, side: THREE.DoubleSide, fog: true,
    }), CAP);
  /**
   * A puff faces the eye, always.
   *
   * It used to be an up-facing pancake, on the reasoning that the camera is
   * mostly above. That is true of a god surveying a valley and false of the
   * one moment the column matters most — flying low, looking across the
   * country, asking whether anybody lives over there. Edge-on, a pancake is
   * nothing at all. Camera-aligned, one shared quaternion serves every puff
   * in the world, so it costs a quaternion copy and reads from any angle.
   */
  const smoke = new THREE.InstancedMesh(new THREE.PlaneGeometry(1, 1),
    new THREE.MeshBasicMaterial({
      map: smokeTexture(), transparent: true, opacity: 0.72, depthWrite: false,
      side: THREE.DoubleSide, fog: true,
    }), SMOKE_CAP);

  stones.name = 'stones'; storages.name = 'storages';
  embers.name = 'embers'; smoke.name = 'smoke';
  woodpiles.name = 'woodpiles'; frames.name = 'frames'; windbreaks.name = 'windbreaks';
  embers.renderOrder = 1;
  smoke.renderOrder = 2;   // over the ember, so the column reads as dimming it

  const meshes = [
    stones, ...shelterKinds.map((k) => shelterMesh[k]), storages,
    embers, smoke, woodpiles, frames, windbreaks,
  ];
  const white = new THREE.Color(1, 1, 1);
  for (const m of meshes) {
    m.frustumCulled = false;
    m.count = 0;
    m.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    // setColorAt allocates lazily — do it now so update never allocates
    for (let i = 0; i < m.instanceMatrix.count; i++) m.setColorAt(i, white);
    m.instanceColor!.setUsage(THREE.DynamicDrawUsage);
    group.add(m);
  }

  // per-frame scratch — the update path allocates nothing
  const _m = new THREE.Matrix4(), _q = new THREE.Quaternion(), _e = new THREE.Euler();
  const _p = new THREE.Vector3(), _s = new THREE.Vector3(), _c = new THREE.Color();
  // the fires in view, gathered in one pass so shelters and props can orient on
  // the camp they belong to. A settlement reads as a settlement because
  // everything in it faces the same middle.
  const fireX = new Float64Array(CAP), fireZ = new Float64Array(CAP);
  let nFire = 0;

  // weathered instance colour: per-id variation, then the ruin's grey collapse
  const tint = (r: number, g: number, b: number, h: number, ruined: boolean): THREE.Color => {
    // one brightness for all three channels: jittering them independently
    // swings the hue, and a hide lean-to came out salmon pink. Weathering
    // makes a shelter lighter or darker, never a different colour.
    const v = 0.82 + h * 0.36;
    const warm = 1 + (((h * 7) % 1) - 0.5) * 0.10;   // a whisper of hue variation
    let rr = r * v * warm;
    let gg = g * v;
    let bb = b * v / warm;
    if (ruined) {
      const l = (rr + gg + bb) / 3;
      rr = lerp(rr, l, 0.7) * 0.5; gg = lerp(gg, l, 0.7) * 0.5; bb = lerp(bb, l, 0.7) * 0.5;
    }
    return _c.setRGB(rr, gg, bb);
  };

  /** Which way is the fire, from here? NaN when this thing stands alone. */
  const fireYaw = (x: number, z: number): number => {
    let best = -1, bestD = CAMP_REACH;
    for (let i = 0; i < nFire; i++) {
      const d = Math.hypot(fireX[i] - x, fireZ[i] - z);
      if (d < bestD) { bestD = d; best = i; }
    }
    // atan2(dx, dz): the yaw that swings local +Z onto that bearing
    return best < 0 ? NaN : Math.atan2(fireX[best] - x, fireZ[best] - z);
  };

  /** One prop, placed. Returns the mesh's new instance count. */
  const prop = (
    mesh: THREE.InstancedMesh, n: number,
    x: number, y: number, z: number, yaw: number,
    sc: number, sy: number, lean: number, col: THREE.Color,
  ): number => {
    if (n >= PROP_CAP) return n;
    _p.set(x, y, z);
    _e.set(0, yaw, lean); _q.setFromEuler(_e);
    _s.set(sc, sc * sy, sc);
    _m.compose(_p, _q, _s);
    mesh.setMatrixAt(n, _m);
    mesh.setColorAt(n, col);
    return n + 1;
  };

  const _roll = new THREE.Quaternion();

  return {
    group,
    update(tSec, views, camR, eye) {
      const near = camR < NEAR;
      const propsNear = camR < PROP_NEAR;

      nFire = 0;
      for (const v of views) {
        if (v.type === 'hearth' && nFire < CAP) {
          fireX[nFire] = v.x; fireZ[nFire] = v.z; nFire++;
        }
      }

      // §48: smoke is the distant signal, and it is sized per hearth rather
      // than per camera height — the old rule read the altitude, which says
      // nothing about how far off the fire you are actually looking at is.
      // Flying at three hundred metres and looking eight kilometres down the
      // coast, altitude called for the smallest column there is.
      let nStone = 0, nStore = 0, nEmber = 0, nSmoke = 0;
      let nWood = 0, nFrame = 0, nBreak = 0;
      for (const k of shelterKinds) nShelterOf[k] = 0;

      for (const v of views) {
        const seed = idSeed(v.id);
        const ruined = v.condition < RUIN;

        if (v.type === 'hearth') {
          if (near) {
            // the ring: five or six stones, each its own shape and setting
            const ring = 5 + (hash3(seed, 0, 91) & 1);
            for (let k = 0; k < ring && nStone < STONE_CAP; k++, nStone++) {
              const h1 = hash01(seed, k, 21), h2 = hash01(seed, k, 22), h3 = hash01(seed, k, 23);
              const a = (k / ring) * TAU + (h1 - 0.5) * 0.55;
              const r = (0.78 + h2 * 0.28) * MODEL;
              _p.set(v.x + Math.cos(a) * r, v.y + 0.04 * MODEL, v.z + Math.sin(a) * r);
              _e.set(0, h3 * TAU, 0); _q.setFromEuler(_e);
              const sc = 0.8 + h3 * 0.55;
              _s.set(sc, sc * 0.7, sc * (0.8 + h1 * 0.4));
              _m.compose(_p, _q, _s);
              stones.setMatrixAt(nStone, _m);
              // soot-darkened while the fire lives, plain grey stone otherwise
              const base = v.active && !ruined ? 0.38 : 0.5;
              stones.setColorAt(nStone, tint(base, base * 0.98, base * 0.94, h2, ruined));
            }
            if (v.active && !ruined && nEmber < CAP) {
              const ph = hash01(seed, 1, 31) * TAU;
              const pulse = 0.72 + 0.22 * Math.sin(tSec * 2.7 + ph)
                          + 0.06 * Math.sin(tSec * 7.3 + ph * 2.1);
              _p.set(v.x, v.y + 0.1 * MODEL, v.z);
              _q.identity();
              _s.set(0.8 + pulse * 0.35, 1, 0.8 + pulse * 0.35);
              _m.compose(_p, _q, _s);
              embers.setMatrixAt(nEmber, _m);
              embers.setColorAt(nEmber, _c.setRGB(pulse, 0.4 * pulse, 0.09 * pulse));
              nEmber++;
            }
          }
          // a ruined hearth never smokes — cold ash tells no one anything
          if (v.active && !ruined) {
            // the whole column grows with its own distance from the eye, so
            // what it looks like stops changing once it is far enough away to
            // need the help. Shape, lean and wobble are all in these metres,
            // so a far plume is the same plume, drawn larger.
            const grow = clamp(Math.hypot(v.x - eye.x, v.y - eye.y, v.z - eye.z) / PLUME_HOLD,
                               1, PLUME_MAX);
            const rise = RISE * grow;
            for (let j = 0; j < PUFFS && nSmoke < SMOKE_CAP; j++, nSmoke++) {
              const o1 = hash01(seed, j, 51), o2 = hash01(seed, j, 52), o3 = hash01(seed, j, 53);
              // each puff rides its own phase of the shared cycle: the column
              // recycles forever, stateless, a pure function of the clock
              const u = (tSec / CYCLE + o1) % 1;
              // grow in fast, fade out by shrinking — opacity stays fixed and cheap
              const env = Math.min(1, u * 6) * (1 - smoothstep(0.7, 1, u));
              const h = u * rise * (0.85 + o2 * 0.3);
              const wob = Math.sin(tSec * 0.5 + o3 * TAU + h * 0.2) * (0.3 + u * 1.3) * grow;
              _p.set(v.x + LEAN_X * h + wob, v.y + 1.1 * MODEL * grow + h,
                     v.z + LEAN_Z * h + wob * 0.6);
              // the camera's own plane, spun about the view axis so no two
              // puffs wear the texture the same way round
              _e.set(0, 0, o2 * TAU); _roll.setFromEuler(_e);
              _q.copy(eye.quaternion).multiply(_roll);
              const size = (2.7 + u * 11.7) * env * grow;
              _s.set(size, size, 1);
              _m.compose(_p, _q, _s);
              smoke.setMatrixAt(nSmoke, _m);
              // firelit at the base, cooling to woodsmoke pale above. Pale is
              // the point: this has to separate from sunlit grass, and a
              // mid-grey at half opacity simply does not.
              const wt = Math.max(0, 1 - u * 3);
              smoke.setColorAt(nSmoke, _c.setRGB(lerp(0.80, 0.86, wt), 0.79, lerp(0.80, 0.70, wt)));
            }
          }
          // fuel beside the fire, tangential to the ring, because that is how
          // anyone who has ever carried wood puts it down
          if (propsNear) {
            const g1 = hash01(seed, 4, 81), g2 = hash01(seed, 5, 81);
            const piles = g2 > 0.58 ? 2 : 1;
            for (let k = 0; k < piles; k++) {
              const hk = hash01(seed, k, 82), hr = hash01(seed, k, 83);
              const a = (g1 + k * 0.41 + hk * 0.16) * TAU;
              const r = (1.75 + hr * 0.5) * MODEL;
              nWood = prop(woodpiles, nWood,
                v.x + Math.cos(a) * r, v.y, v.z + Math.sin(a) * r,
                a + Math.PI / 2 + (hk - 0.5) * 0.5,
                0.9 + hr * 0.25, ruined ? 0.5 : 1,
                ruined ? (hk - 0.5) * 0.5 : (hk - 0.5) * 0.06,
                tint(0.42, 0.32, 0.21, hr, ruined));
            }
          }
        } else if (v.type === 'shelter') {
          // the doorway looks at the fire; a shelter with no fire in reach
          // keeps its own hashed bearing
          const h1 = hash01(seed, 1, 61), h2 = hash01(seed, 2, 61), h3 = hash01(seed, 3, 61);
          const fy = fireYaw(v.x, v.z);
          const yaw = Number.isNaN(fy) ? h1 * TAU : fy + (h1 - 0.5) * 0.24;
          // which roof these hands raised (§50). Read off the structure, never
          // off its owner: §30 unowns an abandoned building, and a ruin keeps
          // the shape it was built in — that is what makes it archaeology.
          const roof = v.built && v.built in SHELTER_GEOMETRY
            ? v.built : DEFAULT_BUILDING;
          const mesh = shelterMesh[roof];
          if (near && nShelterOf[roof] < CAP) {
            _p.set(v.x, v.y, v.z);
            _e.set(0, yaw, (h2 - 0.5) * 0.1); _q.setFromEuler(_e);
            // slightly irregular per id; a ruin sinks to half height
            _s.set(0.85 + h2 * 0.35,
                   (0.85 + h3 * 0.3) * (ruined ? 0.45 : 1),
                   0.85 + h3 * 0.35);
            _m.compose(_p, _q, _s);
            mesh.setMatrixAt(nShelterOf[roof], _m);
            mesh.setColorAt(nShelterOf[roof], tint(0.46, 0.35, 0.24, h1, ruined));
            nShelterOf[roof]++;
          }
          if (propsNear) {
            // a drying frame beside the door, angled to the fire
            const g1 = hash01(seed, 6, 84), g2 = hash01(seed, 7, 84);
            if (g1 > 0.42) {
              const a = yaw + (g2 - 0.5) * 1.8;
              const r = (2.4 + g2 * 0.9) * MODEL;
              nFrame = prop(frames, nFrame,
                v.x + Math.sin(a) * r, v.y, v.z + Math.cos(a) * r,
                a + Math.PI / 2, 0.9 + g1 * 0.22, ruined ? 0.5 : 1,
                ruined ? (g2 - 0.5) * 0.6 : (g2 - 0.5) * 0.07,
                tint(0.52, 0.43, 0.30, g2, ruined));
            }
            // and a windbreak behind it, back to the camp
            const g3 = hash01(seed, 8, 84), g4 = hash01(seed, 9, 84);
            if (g3 > 0.56) {
              const a = yaw + Math.PI + (g4 - 0.5) * 0.8;
              const r = (1.9 + g4 * 0.6) * MODEL;
              nBreak = prop(windbreaks, nBreak,
                v.x + Math.sin(a) * r, v.y, v.z + Math.cos(a) * r,
                a, 0.92 + g3 * 0.2, ruined ? 0.5 : 1,
                ruined ? (g4 - 0.5) * 0.55 : (g4 - 0.5) * 0.06,
                tint(0.47, 0.37, 0.26, g4, ruined));
            }
          }
        } else {
          const h1 = hash01(seed, 1, 71), h2 = hash01(seed, 2, 71);
          if (near && nStore < CAP) {
            _p.set(v.x, v.y, v.z);
            _e.set(0, h1 * TAU, 0); _q.setFromEuler(_e);
            const sc = 0.88 + h2 * 0.28;
            _s.set(sc, sc * (ruined ? 0.8 : 1), sc);   // a ruined rack sags
            _m.compose(_p, _q, _s);
            storages.setMatrixAt(nStore, _m);
            storages.setColorAt(nStore, tint(0.5, 0.4, 0.27, h2, ruined));
            nStore++;
          }
          // a granary is where the work happens: timber stacked against it and
          // a frame alongside for whatever is curing
          if (propsNear) {
            const g1 = hash01(seed, 6, 86), g2 = hash01(seed, 7, 86);
            if (g1 > 0.32) {
              const a = h1 * TAU + 1.9 + (g1 - 0.5) * 0.7;
              const r = (1.5 + g1 * 0.5) * MODEL;
              nWood = prop(woodpiles, nWood,
                v.x + Math.cos(a) * r, v.y, v.z + Math.sin(a) * r,
                h1 * TAU + Math.PI / 2, 0.88 + g1 * 0.24, ruined ? 0.5 : 1,
                ruined ? (g1 - 0.5) * 0.5 : (g1 - 0.5) * 0.06,
                tint(0.42, 0.32, 0.21, g1, ruined));
            }
            if (g2 > 0.5) {
              const a = h1 * TAU - 1.5 + (g2 - 0.5) * 0.7;
              const r = (2.2 + g2 * 0.7) * MODEL;
              nFrame = prop(frames, nFrame,
                v.x + Math.cos(a) * r, v.y, v.z + Math.sin(a) * r,
                h1 * TAU, 0.9 + g2 * 0.2, ruined ? 0.5 : 1,
                ruined ? (g2 - 0.5) * 0.6 : (g2 - 0.5) * 0.07,
                tint(0.52, 0.43, 0.30, g2, ruined));
            }
          }
        }
      }

      stones.count = nStone; storages.count = nStore;
      embers.count = nEmber; smoke.count = nSmoke;
      woodpiles.count = nWood; frames.count = nFrame; windbreaks.count = nBreak;
      for (const k of shelterKinds) {
        shelterMesh[k].count = nShelterOf[k];
        // a roof nobody in view builds costs nothing but stays in the scene:
        // the next camp over may be the people who do
        shelterMesh[k].visible = near && nShelterOf[k] > 0;
      }
      stones.visible = storages.visible = embers.visible = near;
      woodpiles.visible = frames.visible = windbreaks.visible = propsNear;
      smoke.visible = nSmoke > 0;
      for (const m of meshes) {
        m.instanceMatrix.needsUpdate = true;
        m.instanceColor!.needsUpdate = true;
      }
    },
  };
}
